import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tooltip,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit, Delete, Refresh, Warning, ChevronLeft, ChevronRight, Visibility, VisibilityOff } from '@mui/icons-material';
import { 
  fetchSettings, 
  createSetting, 
  updateSetting, 
  deleteSetting, 
  clearError, 
  clearSuccess 
} from '../../store/slices/settingsSlice';

const defaultForm = {
  key: '',
  value: '',
  sensitive_value: '',
  hint: '',
  is_sensitive: false
};

const Settings = () => {
  const { t } = useTranslation(['settings', 'common']);
  const dispatch = useDispatch();
  const { settings, loading, error, totalSettings, success } = useSelector((state) => state.settings);
  
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showSensitiveValues, setShowSensitiveValues] = useState(false);
  
  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({ open: false, settingId: null, settingKey: '' });
  
  // Filter states
  const [filters, setFilters] = useState({
    key: '',
    hint: '',
    is_sensitive: ''
  });
  const [isFiltering, setIsFiltering] = useState(false);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Calculate total pages
  const totalPages = Math.ceil(totalSettings / rowsPerPage) || 1;

  // Fetch settings with pagination
  const fetchSettingsData = useCallback((pageArg, rowsArg, filtersArg) => {
    dispatch(fetchSettings({
      ...filtersArg,
      page: pageArg + 1,
      page_size: rowsArg,
      ordering: 'key',
    }));
  }, [dispatch]);

  // Debounced filter effect for real-time search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const activeFilters = {};
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          activeFilters[key] = filters[key];
        }
      });
      setIsFiltering(Object.keys(activeFilters).length > 0);
      setPage(0);
      fetchSettingsData(0, rowsPerPage, activeFilters); // Use 0 directly, not page variable
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filters, rowsPerPage, fetchSettingsData]); // Remove 'page' from dependencies

  // Separate effect for pagination changes (immediate, no debounce)
  useEffect(() => {
    const activeFilters = {};
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        activeFilters[key] = filters[key];
      }
    });
    fetchSettingsData(page, rowsPerPage, activeFilters);
  }, [page]); // Only trigger when page changes

  // Handle success and error messages
  useEffect(() => {
    if (success) {
      setSnackbar({ open: true, message: t('settings:messages.operationCompletedSuccess'), severity: 'success' });
      dispatch(clearSuccess());
      handleClose();
    }
  }, [success, dispatch, t]);

  useEffect(() => {
    if (error) {
      // Handle error that might be an object
      let errorMessage = error;
      if (typeof error === 'object') {
        if (error.detail) {
          errorMessage = error.detail;
        } else if (error.message) {
          errorMessage = error.message;
        } else if (error.value) {
          errorMessage = error.value;
        } else {
          errorMessage = JSON.stringify(error);
        }
      }
      setSnackbar({ open: true, message: String(errorMessage), severity: 'error' });
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Pagination handlers
  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
    }
  };

  const handleCustomRowsSubmit = () => {
    const value = parseInt(customRowsInput);
    if (value > 0) {
      setRowsPerPage(value);
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  // Form handlers
  const handleOpen = (setting = null) => {
    if (setting) {
      setEditId(setting.id);
      setForm({
        key: setting.key || '',
        value: String(setting.value || ''),
        sensitive_value: String(setting.sensitive_value || ''),
        hint: setting.hint || '',
        is_sensitive: Boolean(setting.is_sensitive)
      });
    } else {
      setEditId(null);
      setForm(defaultForm);
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditId(null);
    setForm(defaultForm);
  };

  const handleSubmit = () => {
    const formData = { ...form };
    
    // Ensure all string values are properly converted
    formData.key = String(formData.key || '');
    formData.value = String(formData.value || '');
    formData.sensitive_value = String(formData.sensitive_value || '');
    formData.hint = String(formData.hint || '');
    formData.is_sensitive = Boolean(formData.is_sensitive);
    
    // Clean up empty values
    if (!formData.sensitive_value || formData.sensitive_value === '') delete formData.sensitive_value;
    if (!formData.hint || formData.hint === '') delete formData.hint;
    if (!formData.value || formData.value === '') delete formData.value;
    
    // Validate that at least one value field is provided
    if (!formData.value && !formData.sensitive_value) {
      setSnackbar({ 
        open: true, 
        message: t('settings:messages.pleaseProvideValueOrSensitiveValue'), 
        severity: 'error' 
      });
      return;
    }
    
    
    
    if (editId) {
      dispatch(updateSetting({ id: editId, data: formData }));
    } else {
      dispatch(createSetting(formData));
    }
  };

  const handleDelete = (setting) => {
    setDeleteModal({ open: true, settingId: setting.id, settingKey: setting.key });
  };

  const confirmDelete = () => {
    dispatch(deleteSetting(deleteModal.settingId));
    setDeleteModal({ open: false, settingId: null, settingKey: '' });
  };

  // Filter handlers
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      key: '',
      hint: '',
      is_sensitive: ''
    });
    setIsFiltering(false);
    setPage(0);
  };

  // Add serial numbers to settings for the current page and map display fields
  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      id: row.id || `temp-${idx}`, // Ensure id is always present
      sn: (page * rowsPerPage) + idx + 1,
      value_display: row.value || '',
      sensitive_value_display: row.sensitive_value || 'N/A'
    }));

  // DataGrid columns
  const columns = [
    { 
      field: 'sn', 
      headerName: t('settings:table.sn'), 
      width: 80, 
      sortable: false, 
      filterable: false,
      align: 'center',
      headerAlign: 'center'
    },
    {
      field: 'key',
      headerName: t('settings:table.settingKey'),
      flex: 1,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'value_display',
      headerName: t('settings:table.value'),
      flex: 1,
      minWidth: 250,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          fontFamily: 'monospace',
          backgroundColor: params.row.is_sensitive ? '#fff3cd' : 'transparent',
          padding: '2px 6px',
          borderRadius: '4px',
          wordBreak: 'break-word',
          maxWidth: '100%'
        }}>
          {params.value}
        </Typography>
      )
    },
    {
      field: 'sensitive_value_display',
      headerName: t('settings:table.sensitiveValue'),
      flex: 1,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ 
          fontFamily: 'monospace',
          padding: '2px 6px',
          borderRadius: '4px'
        }}>
          {params.value || t('settings:status.na')}
        </Typography>
      )
    },
    {
      field: 'hint',
      headerName: t('settings:table.hint'),
      flex: 1,
      minWidth: 200,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          color="textSecondary"
          title={params.value || '-'}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.2em',
            maxHeight: '2.4em'
          }}
        >
          {params.value || '-'}
        </Typography>
      )
    },
    {
      field: 'is_sensitive',
      headerName: t('settings:table.sensitive'),
      flex: 1,
      minWidth: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ? t('settings:status.yes') : t('settings:status.no')}
          color={params.value ? 'warning' : 'default'}
          size="small"
        />
      )
    },
    {
      field: 'created_at',
      headerName: t('settings:table.created'),
      flex: 1,
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2" color="textSecondary">
          {new Date(params.value).toLocaleDateString()}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: t('settings:table.actions'),
      width: 120,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('settings:tooltips.edit')}>
            <IconButton
              size="small"
              onClick={() => handleOpen(params.row)}
              color="primary"
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('settings:tooltips.delete')}>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row)}
              color="error"
            >
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('settings:title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => {
              const activeFilters = {};
              Object.keys(filters).forEach(key => {
                if (filters[key]) {
                  activeFilters[key] = filters[key];
                }
              });
              fetchSettingsData(page, rowsPerPage, activeFilters);
            }}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('settings:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('settings:addSetting')}
          </Button>
        </Box>
      </Box>

      {/* Filter Section */}
      <Box sx={{ mb: 2, p: 2, background: 'white', borderRadius: 2, boxShadow: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#DC143C', fontWeight: 'bold' }}>
              {t('settings:filters.title')}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {t('settings:filters.description')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="textSecondary">
              {t('settings:filters.showingSettings', { count: settings.length })}
              {isFiltering && (
                <span> {t('settings:filters.filtered')}</span>
              )}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={clearFilters}
              disabled={!Object.values(filters).some(f => f)}
            >
              {t('settings:filters.clearFilters')}
            </Button>
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
          <TextField
            label={t('settings:filters.searchSettingKey')}
            value={filters.key}
            onChange={(e) => handleFilterChange('key', e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label={t('settings:filters.searchHint')}
            value={filters.hint}
            onChange={(e) => handleFilterChange('hint', e.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            select
            label={t('settings:filters.filterBySensitivity')}
            value={filters.is_sensitive}
            onChange={(e) => handleFilterChange('is_sensitive', e.target.value)}
            size="small"
            fullWidth
          >
            <MenuItem value="">{t('settings:filters.allSettings')}</MenuItem>
            <MenuItem value="true">{t('settings:filters.sensitiveOnly')}</MenuItem>
            <MenuItem value="false">{t('settings:filters.nonSensitiveOnly')}</MenuItem>
          </TextField>
        </Box>
      </Box>

      <Box sx={{ height: 520, width: '100%', background: 'white', borderRadius: 2, boxShadow: 1 }}>
        {settings.length === 0 && !loading && !isFiltering ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
              {t('settings:emptyState.noSettingsFound')}
            </Typography>
            <Button variant="outlined" onClick={() => {
              const activeFilters = {};
              Object.keys(filters).forEach(key => {
                if (filters[key]) {
                  activeFilters[key] = filters[key];
                }
              });
              fetchSettingsData(page, rowsPerPage, activeFilters);
            }}>
              {t('settings:emptyState.refreshData')}
            </Button>
          </Box>
        ) : (
                     <DataGrid
            rows={addSerialNumbers(settings, page, rowsPerPage)}
            columns={columns}
            pageSize={rowsPerPage}
            rowsPerPageOptions={[]}
            disableSelectionOnClick
            loading={loading}
            hideFooter
                          getRowId={(row) => row.id || row.key || `temp-${row.sn || Math.random()}`}
            sx={{
              border: 'none',
              '& .MuiDataGrid-columnHeader': {
                backgroundColor: theme => theme.palette.primary.main,
              },
              '& .MuiDataGrid-columnHeaderTitle': {
                color: 'white',
                fontWeight: 'bold',
                fontSize: '1rem'
              },
              '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                color: 'white'
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: '#FFF0F3'
              },
              '& .MuiDataGrid-cell': {
                fontSize: '1rem',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                minHeight: '52px'
              },
              '& .MuiDataGrid-row': {
                minHeight: '52px !important',
                maxHeight: 'none !important'
              }
            }}
          />
        )}
      </Box>

      {/* Custom Pagination Bar */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
        <IconButton onClick={handlePrevPage} disabled={page === 0}>
          <ChevronLeft />
        </IconButton>
        <Typography variant="body2">
          {t('settings:pagination.page', { current: totalPages === 0 ? 0 : page + 1, total: totalPages })}
        </Typography>
        <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
          <ChevronRight />
        </IconButton>
        <Typography variant="body2" sx={{ ml: 2 }}>
          {t('settings:pagination.rowsPerPage')}
        </Typography>
        {!customRowsActive ? (
          <TextField
            select
            size="small"
            value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
            onChange={handleRowsPerPageChange}
            sx={{ width: 100 }}
          >
            {[10, 20, 50, 100].map((option) => (
              <MenuItem key={option} value={option}>{option}</MenuItem>
            ))}
            <MenuItem value="custom">{t('settings:pagination.custom')}</MenuItem>
          </TextField>
        ) : (
          <TextField
            size="small"
            value={customRowsInput}
            onChange={(e) => setCustomRowsInput(e.target.value.replace(/\D/g, ''))}
            onBlur={handleCustomRowsSubmit}
            autoFocus
            sx={{ width: 100 }}
            inputProps={{ min: 1, style: { textAlign: 'center' } }}
            placeholder={t('settings:pagination.rows')}
          />
        )}
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('settings:dialogs.editTitle') : t('settings:dialogs.addTitle')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label={t('settings:dialogs.settingKey')}
              value={String(form.key || '')}
              onChange={(e) => setForm(prev => ({ ...prev, key: e.target.value }))}
              fullWidth
              required
              disabled={!!editId} // Key cannot be changed when editing
            />
            
            <TextField
              label={t('settings:dialogs.value')}
              value={String(form.value || '')}
              onChange={(e) => setForm(prev => ({ ...prev, value: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            
            <TextField
              label={t('settings:dialogs.sensitiveValue')}
              value={String(form.sensitive_value || '')}
              onChange={(e) => setForm(prev => ({ ...prev, sensitive_value: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              helperText={t('settings:dialogs.sensitiveValueHelper')}
            />
            
            <TextField
              label={t('settings:dialogs.hint')}
              value={String(form.hint || '')}
              onChange={(e) => setForm(prev => ({ ...prev, hint: e.target.value }))}
              fullWidth
              helperText={t('settings:dialogs.hintHelper')}
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={form.is_sensitive}
                  onChange={(e) => setForm(prev => ({ ...prev, is_sensitive: e.target.checked }))}
                />
              }
              label={t('settings:dialogs.markAsSensitive')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('settings:dialogs.cancel')}</Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained"
            disabled={!String(form.key || '').trim() || (!String(form.value || '').trim() && !String(form.sensitive_value || '').trim())}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {editId ? t('settings:dialogs.update') : t('settings:dialogs.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteModal.open} 
        onClose={() => setDeleteModal({ open: false, settingId: null, settingKey: '' })}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          color: '#DC143C', 
          fontWeight: 'bold',
          pb: 1
        }}>
          <Warning sx={{ color: '#DC143C', fontSize: 28 }} />
          {t('settings:deleteDialog.title')}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, color: '#333' }}>
            {t('settings:deleteDialog.message', { key: deleteModal.settingKey })}
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ 
            backgroundColor: '#FFF3E0', 
            p: 2, 
            borderRadius: 1, 
            border: '1px solid #FFB74D',
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <Warning sx={{ color: '#F57C00', fontSize: 20 }} />
            {t('settings:deleteDialog.warning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={() => setDeleteModal({ open: false, settingId: null, settingKey: '' })}
            variant="outlined"
            sx={{ 
              borderColor: '#666', 
              color: '#666',
              '&:hover': { 
                borderColor: '#333', 
                backgroundColor: '#f5f5f5' 
              }
            }}
          >
            {t('settings:deleteDialog.cancel')}
          </Button>
          <Button 
            onClick={confirmDelete}
            variant="contained"
            color="error"
            sx={{ 
              backgroundColor: '#DC143C', 
              '&:hover': { backgroundColor: '#B91C3C' },
              px: 3
            }}
            startIcon={<Delete />}
          >
            {t('settings:deleteDialog.delete')}
          </Button>
        </DialogActions>
      </Dialog>

             {/* Snackbar */}
       <Snackbar
         open={snackbar.open}
         autoHideDuration={6000}
         onClose={() => setSnackbar({ ...snackbar, open: false })}
         anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
       >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {String(snackbar.message || '')}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings; 